// The admin shell (APS-412, Mosaic's direction): Console D's shell, Settings-style. The sidebar has
// the wordmark with ADMIN, the pages in three groups (Clientes, Dinheiro, Operação) and the staff
// member at its foot, whose menu holds the language and signing out. The bar holds the crumbs and
// Find, which goes to the organizations' search. Under 640 px the sidebar is the console's drawer.
// A page adds its own trailing crumbs (useCrumbs): the object it shows, ".../Organizações/Acme".

import {
  findKey,
  Icon,
  type IconName,
  Mark,
  readRail,
  Seg,
  saveRail,
  Tag,
  usePopover,
} from '@apsio/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import { meQuery, setLocale, signOut } from '../api/queries'
import { isLocale, type Locale, type MessageId, useI18n } from '../i18n'

type NavPath =
  | '/overview'
  | '/orgs'
  | '/erasures'
  | '/abuse'
  | '/audit'
  | '/revenue'
  | '/margin'
  | '/cost-model'
  | '/plan-fit'
  | '/quote'
  | '/nfse'
  | '/health'
  | '/infra'
  | '/system'
  | '/product-flags'
  | '/webhooks'

const NAV: { group: MessageId; items: [NavPath, IconName, MessageId][] }[] = [
  {
    group: 'shell.group.clients',
    items: [
      ['/overview', 'home', 'nav.overview'],
      ['/orgs', 'org', 'nav.orgs'],
      ['/erasures', 'shield', 'nav.erasures'],
      ['/abuse', 'flag', 'nav.abuse'],
      ['/audit', 'history', 'nav.audit'],
    ],
  },
  {
    group: 'shell.group.money',
    items: [
      ['/revenue', 'card', 'nav.revenue'],
      ['/margin', 'perf', 'nav.margin'],
      ['/cost-model', 'sliders', 'nav.costModel'],
      ['/plan-fit', 'stack', 'nav.planFit'],
      ['/quote', 'file', 'nav.quote'],
      ['/nfse', 'book', 'nav.nfse'],
    ],
  },
  {
    group: 'shell.group.ops',
    items: [
      ['/health', 'pulse', 'nav.health'],
      ['/infra', 'chip', 'nav.infra'],
      ['/system', 'logs', 'nav.system'],
      ['/product-flags', 'toggle', 'nav.flags'],
      ['/webhooks', 'plug', 'nav.webhooks'],
    ],
  },
]

/** The group and page the path is in, for the sidebar's mark and the bar's crumbs. */
function where(path: string) {
  for (const g of NAV)
    for (const [to, , label] of g.items)
      if (path === to || path.startsWith(`${to}/`)) return { group: g.group, to, label }
  // A person's erasure belongs to Exclusões (an organization's view is under /orgs already).
  if (path.startsWith('/users/'))
    return {
      group: 'shell.group.clients' as const,
      to: '/erasures' as const,
      label: 'nav.erasures' as const,
    }
  return undefined
}

/** A crumb after the page's own: the object a page shows, a link when another follows. */
export interface Crumb {
  label: string
  to?: string
}
const CrumbsContext = createContext<(trail: Crumb[]) => void>(() => {})

/**
 * The page's trailing crumbs, after its nav page ("Organizações / Acme / Ver como o cliente").
 * Set while the page is mounted; under 640 px the bar shows the last one alone.
 */
export function useCrumbs(trail: Crumb[]) {
  const set = useContext(CrumbsContext)
  const key = JSON.stringify(trail)
  // biome-ignore lint/correctness/useExhaustiveDependencies: the trail by value, not identity
  useEffect(() => {
    set(trail)
    return () => set([])
  }, [set, key])
}

export function Shell() {
  const { t } = useI18n()
  const path = useLocation({ select: (l) => l.pathname })
  const [drawer, setDrawer] = useState(false)
  // A new page closes the drawer; so does Esc.
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes on every path change
  useEffect(() => setDrawer(false), [path])
  useEffect(() => {
    if (!drawer) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawer])
  // The sidebar folds to a rail of icons from the bar's button or `[` (brand v2, as the console).
  const [rail, setRail] = useState(readRail)
  const toggleRail = useCallback(() => {
    setRail((r) => {
      saveRail(!r)
      return !r
    })
  }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return
      const el = e.target as HTMLElement | null
      if (el?.closest('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      toggleRail()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [toggleRail])
  const here = where(path)
  const [trail, setTrail] = useState<Crumb[]>([])
  const crumbs = useMemo(
    () => [...(here ? [{ label: t(here.label), to: here.to as string }] : []), ...trail],
    [here, trail, t],
  )
  const navigate = useNavigate()
  // Find searches the organizations: their page, with its search field focused.
  const find = useCallback(async () => {
    await navigate({ to: '/orgs' })
    requestAnimationFrame(() =>
      document.querySelector<HTMLInputElement>('#main input[type="search"]')?.focus(),
    )
  }, [navigate])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        void find()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [find])

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('shell.skip')}
      </a>
      <aside
        className={drawer ? 'nav open' : 'nav'}
        id="nav"
        aria-label={t('shell.sidebar')}
        data-mode="settings"
      >
        <div className="nav-top">
          <Link to="/overview" className="nav-brand" aria-label={t('shell.home')}>
            <Mark />
            <span translate="no">apsio</span>
          </Link>
          <Tag>{t('shell.badge')}</Tag>
        </div>
        <nav className="nav-set" aria-label={t('shell.nav')}>
          {NAV.map((g) => (
            <div key={g.group}>
              <p className="nav-g">{t(g.group)}</p>
              {g.items.map(([to, icon, label]) => (
                <Link
                  key={to}
                  className="nav-a"
                  to={to}
                  aria-current={here?.to === to ? 'page' : undefined}
                  title={t(label)}
                >
                  <Icon name={icon} />
                  <span className="lbl">{t(label)}</span>
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="nav-foot">
          <StaffMenu path={path} />
        </div>
      </aside>
      {drawer ? (
        // biome-ignore lint/a11y/noStaticElementInteractions: the scrim closes the drawer; Esc does too
        // biome-ignore lint/a11y/useKeyWithClickEvents: Esc closes it (above)
        <div className="nav-scrim" onClick={() => setDrawer(false)} />
      ) : null}
      <div className="main">
        <header className="bar">
          <button
            type="button"
            className="btn ghost sm bar-menu"
            aria-label={t('shell.menu')}
            aria-expanded={drawer}
            aria-controls="nav"
            onClick={() => setDrawer(!drawer)}
          >
            <Icon name="menu" />
          </button>
          <button
            type="button"
            className="btn ghost sm bar-side"
            aria-controls="nav"
            aria-expanded={!rail}
            onClick={toggleRail}
            title={rail ? t('shell.railOpenTitle') : t('shell.railTitle')}
          >
            <Icon name="sidebar" />
            <span className="vh">{rail ? t('shell.railOpen') : t('shell.rail')}</span>
          </button>
          {/* Under 640 px the bar shows the page alone: .scope-org (the leading crumbs) hides. */}
          <nav className="scope" aria-label={t('crumbs')}>
            <span className="scope-org">
              <span>{t('shell.root')}</span>
              {here ? (
                <>
                  <span className="sep" aria-hidden="true">
                    /
                  </span>
                  <span>{t(here.group)}</span>
                </>
              ) : null}
              {crumbs.slice(0, -1).map((c) => (
                <Fragment key={`${c.label}${c.to ?? ''}`}>
                  <span className="sep" aria-hidden="true">
                    /
                  </span>
                  {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
                </Fragment>
              ))}
              {crumbs.length ? (
                <span className="sep" aria-hidden="true">
                  /
                </span>
              ) : null}
            </span>
            {crumbs.length ? (
              <b className="scope-a" aria-current="page">
                {crumbs[crumbs.length - 1]?.label}
              </b>
            ) : null}
          </nav>
          <div className="bar-r">
            <button type="button" className="search" onClick={find}>
              <Icon name="search" />
              <span className="lbl">{t('shell.find')}</span>
              <kbd translate="no">{findKey()}</kbd>
            </button>
          </div>
        </header>
        <CrumbsContext.Provider value={setTrail}>
          <Outlet />
        </CrumbsContext.Provider>
      </div>
    </div>
  )
}

/** "Ana Acme" → "AA"; an address alone → its first letter. */
function initials(name: string, email: string) {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const from = words.length > 0 ? words : [email]
  return from
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

/** The staff member at the sidebar's foot (the console's account menu): language, sign out. */
function StaffMenu({ path }: { path: string }) {
  const { t } = useI18n()
  const me = useQuery(meQuery)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const pop = usePopover(path, 'above')
  const popId = useId()
  if (!me.data) return null
  const staff = me.data
  const name = staff.name || staff.email

  async function changeLanguage(value: string) {
    if (!isLocale(value)) return
    const updated = await setLocale(value as Locale)
    qc.setQueryData(meQuery.queryKey, updated)
  }

  async function leave() {
    const url = await signOut().catch(() => null)
    qc.clear()
    if (url) window.location.assign(url)
    else await navigate({ to: '/sign-in' })
  }

  return (
    <div className="nav-me">
      <button
        ref={pop.anchor}
        type="button"
        className="nav-user"
        aria-expanded={pop.open}
        aria-controls={popId}
        title={name}
        onClick={pop.toggle}
      >
        <span className="avatar" aria-hidden="true">
          {initials(staff.name, staff.email)}
        </span>
        <span className="who">
          <b translate="no">{staff.email}</b>
          <span>{t('shell.sessionUntil', { time: new Date(staff.session.expires_at) })}</span>
        </span>
        <Icon name="updown" small />
      </button>
      {pop.open ? (
        <section
          ref={pop.pop}
          id={popId}
          className="pop me-pop"
          style={pop.style}
          aria-label={t('shell.staffMenu')}
        >
          <div className="me-h">
            <span className="avatar" aria-hidden="true">
              {initials(staff.name, staff.email)}
            </span>
            <span className="who">
              <b>{name}</b>
              <span translate="no">{staff.email}</span>
            </span>
          </div>
          <hr />
          <div className="me-row">
            <span>{t('shell.language')}</span>
            <Seg
              label={t('shell.language')}
              value={staff.locale}
              onChange={(v) => changeLanguage(v)}
              options={[
                { value: 'pt-BR', label: t('language.ptBR') },
                { value: 'en', label: t('language.en') },
              ]}
            />
          </div>
          <hr />
          <button type="button" className="pal-i" onClick={leave}>
            <Icon name="out" />
            <span>{t('shell.signOut')}</span>
          </button>
        </section>
      ) : null}
    </div>
  )
}
